import { authFetch } from '.';
import { IApplication, IDropdownApplications, ILicense, IUser, IUserRes, } from '../models/smp/';


const baseUrl = '/smp/application';

export const SMPAPI = {

  async createEmployeeSmpProfile(payload: { employeeId: number, notes: string, procurementStatus: string, procurementStatusId?: number }) {
    const response = await authFetch.post(`${baseUrl}/employee-smp-profile`, payload);
    return response.data;
  },

  async updateEmployeeSmpProfile(employeeId: number, payload: { notes: string, procurementStatus: string, procurementStatusId?: number }) {
    const response = await authFetch.put(`${baseUrl}/employee-smp-profile/${employeeId}`, payload);
    return response.data;
  },

  async fetchEmployeeSmpProfile(employeeId: number) {
    const response = await authFetch.get(`${baseUrl}/employee-smp-profile/${employeeId}`);
    return response.data;
  },

  async fetchProcurementStatusOptions(): Promise<{ value: number, label: string }[]> {
    const response = await authFetch.get(`${baseUrl}/procurement-status-options`);
    return response.data;
  },
  
  async fetchApplications(options: {
    page?: number;
    perPage?: number;
    search?: string;
    sort?: string;
  }) {
    const { page = 1, perPage = 10, search, sort } = options;
    const qs = new URLSearchParams({
      page: String(page),
      perPage: String(perPage),
      ...(search?.trim() ? { search: search.trim() } : {}),
      ...(sort?.trim() ? { sort: sort.trim() } : {}),
    }).toString();

    const response = await authFetch.get(`${baseUrl}/list?${qs}`);
    return response.data;
  },

  async fetchApplicationTypes(): Promise<IDropdownApplications[]> {
    const response = await authFetch.get(`${baseUrl}/types`);
    return response.data;
  },

  async fetchAllLicenses(params: { page?: number; perPage?: number }): Promise<{ records: ILicense[]; totalRecordCount: number }> {
    const response = await authFetch.get(`${baseUrl}/licenses/all`, { params });
    return response.data;
  },

  async deleteApplication(applicationID: number): Promise<void> {
    await authFetch.delete(`${baseUrl}/${applicationID}`);
  },

  async upsertApplication(application: IApplication): Promise<IApplication> {
    let request: IApplication | FormData = application;
    if (application.file) {
      const { file, ...rest } = application;
      const formData = new FormData();
      for (const key in rest) {
        const value = rest[key as keyof typeof rest];
        if (value != null) {
          formData.append(key, value.toString());
        }
      }
      formData.append("file", file);
      request = formData;
    }

    const url = application.applicationID
      ? `${baseUrl}/update/${application.applicationID}`
      : `${baseUrl}/add`;

    const method = application.applicationID ? "put" : "post";

    const response = await authFetch[method](url, request);
    return response.data;
  },

  async fetchView(): Promise<any> {
    const response = await authFetch.get(`${baseUrl}/view`);
    return response.data;
  },

  async fetchCategories(): Promise<IDropdownApplications[]> {
    const response = await authFetch.get(`${baseUrl}/categories`);
    return response.data;
  },

  async fetchVendors(): Promise<IDropdownApplications[]> {
    const response = await authFetch.get(`${baseUrl}/vendors`);
    return response.data;
  },

  async getApplication(applicationId: number | string): Promise<IApplication> {
    const response = await authFetch.get(`${baseUrl}/view/${applicationId}`);
    
    const data = response.data;
  
    return {
      applicationID: data.applicationID,
      name: data.name,
      TypeID: data.typeID,
      Description: data.description,
      Website: data.website,
      Status: data.status,
      CategoryID: data.categoryID,
      VendorID: data.vendorID ?? undefined,
      OwnerID: data.ownerID ?? undefined,
      LogInCredentialsRequired: data.logInCredentialsRequired ?? false,
      UsageReportRequired: data.usageReportRequired ?? false,
      AdminOption: data.adminOption ?? false,
      SingleSignOn: data.singleSignOn ?? false,
      InvoiceToCorpPayables: data.invoiceToCorpPayables ?? false,
      FSPrepaid: data.fsPrepaid ?? false,
      PublishInCatalog: data.publishInCatalog ?? false,
      SharepointLink: data.sharepointLink ?? '',
      LogoPath: data.logoPath ?? '', 
      AuthorizationStatus: data.authorizationStatus,
      PaymentMethodDetails: data.paymentMethodDetails ?? '', 
      fileId: data.fileId ?? 0,
      file: data.file,
      typeName: data.typeName ?? '',
      categoryName: data.categoryName ?? '',
      vendorName: data.vendorName ?? '',
      CreatedAt: data.createdDate ?? new Date().toISOString(),
      CreatedBy: data.createdBy,
      cancelByDate: data.cancelByDate,
      assignedBundleIds: data.assignedBundleIds || [],
    };
  },


  async fetchEmployeesLookup(search: string): Promise<{ id: number; name: string; email?: string }[]> {
    const params = new URLSearchParams({ name: search });
    const response = await authFetch.get(`${baseUrl}/employee-lookup?${params.toString()}`);
    const result = response.data;

    return result.map((e: any) => ({
      id: e.value,
      name: e.text,
      email: e.email, 
    }));
  },


  async uploadApplicationLogo(file: File): Promise<string> {
    const formData = new FormData();
    formData.append('File', file);
  
    const response = await authFetch.post(`${baseUrl}/upload-logo`, formData);
    return response.data; 
  },

  async getApplicationLogoUrl(fileId: number): Promise<string> {
    const response = await authFetch.get(`/api/ApplicationLogo/sas/${fileId}`);
    return response.data.url;
  },

  getFileIdFromLogoPath(logoPath: string | null | undefined): number | null {
    if (!logoPath) return null;
    
    // Extract the file ID from the path
    const match = logoPath.match(/(\d+)/);
    return match ? parseInt(match[1], 10) : null;
  },

  async saveVendor(payload: { name: string }): Promise<{ vendorID: number }> {
    const response = await authFetch.post(`${baseUrl}/vendor`, payload);
    return response.data;
  },

  async saveApplicationNote(payload: { applicationID: number; noteText: string }): Promise<void> {
    await authFetch.post(`${baseUrl}/note`, payload);
  },

  async fetchApplicationNote(applicationID: number): Promise<string> {
    try {
      const response = await authFetch.get(`${baseUrl}/note/${applicationID}`);
      return response.data;
    } catch (err: any) {
      // If note not found, treat as empty note without surfacing an error to the UI
      if (err?.response?.status === 404) {
        return '';
      }
      throw err;
    }
  },

  async fetchLicenses(
    applicationId: string | number, 
    params: { 
      page?: number; 
      perPage?: number;
      search?: string;
      sort?: string;
    }
  ): Promise<{ records: ILicense[]; totalRecordCount: number }> {

    const queryParams = new URLSearchParams({
      page: String(params.page ?? 1),
      perPage: String(params.perPage ?? 10)
    });

    if (params.search?.trim()) {
      queryParams.append('search', params.search.trim());
    }

    if (params.sort?.trim()) {
      queryParams.append('sort', params.sort.trim());
    }

    const response = await authFetch.get(
      `${baseUrl}/${applicationId}/licenses?${queryParams.toString()}`
    );

    const data = response.data;

    const transformedRecords: ILicense[] = data.records.map((record: any) => ({
      licenseID: record.licenseID.toString(),
      licenseName: record.licenseName || `License ${record.licenseID}`,
      licenseType: record.licenseType || '',
      licenseStatus: record.licenseStatus,
      startDate: record.startDate || '',
      endDate: record.endDate || '',
      cancelByDate: record.cancelByDate || '',
      renewByDate: record.renewByDate || '',
      autoRenew: record.autoRenew || 'No',
      renewalTerm: record.renewalTerm,
      annualCost: record.annualCost?.toString() || '',
      seatQuantity: record.seatQuantity?.toString() || '',
      creditQuantity: record.creditQuantity?.toString() || '',
      totalValue: record.totalValue?.toString() || '',
      monthlyContractValue: record.monthlyContractValue?.toString() || '',
      annualCostPerUnit: record.annualCostPerUnit?.toString() || '',
      paymentTerm: record.paymentTerm,
      paymentMethod: record.paymentMethod,
      initialPaymentDate: record.initialPaymentDate || '',
      approvalNotes: record.approvalNotes || '',
      otherNotes: record.otherNotes || '',
      isContract: record.isContract || false,
      isSubscription: record.isSubscription || false,
      applicationID: record.applicationID || Number(applicationId),
      fileId: record.fileId,
      file: record.file,
      vendor: record.vendor || '',
      cost: record.cost || '',
      licenseKey: record.licenseKey || '',
      isDisabled: record.isDisabled ?? false, 
      unAssigned: !!record.unAssigned,  
    }));
  
    return {
      records: transformedRecords,
      totalRecordCount: data.totalRecordCount,
    };
  },
  

  async deleteLicense(licenseId: string): Promise<void> {
    await authFetch.delete(`${baseUrl}/license/${licenseId}`);
  },

  async fetchApplicationStatuses(): Promise<{ value: string; label: string }[]> {
    const response = await authFetch.get(`${baseUrl}/status-options`);
    return response.data;
  },

  async upsertLicense(license: ILicense): Promise<ILicense> {
   
    const payloadObj: Record<string, any> = {};
    for (const [key, value] of Object.entries(license)) {
      if (key === 'applicationID') {
        payloadObj[key] = value;   
        continue;
      }
      // skip null/undefined for everything else
      if (value == null) continue;
      if (typeof value === 'string' && value.trim() === '') continue;
      if (typeof value === 'number' && value === 0) continue;
      payloadObj[key] = value;
    }
    
  
    // decide JSON vs FormData
    let request: any;
    if (license.file) {
      const formData = new FormData();
      for (const [k, v] of Object.entries(payloadObj)) {
        formData.append(k, v.toString());
      }
      formData.append("file", license.file);
      request = formData;
    } else {
      request = payloadObj;
    }
  
    const url    = license.licenseID
                   ? `${baseUrl}/license/${license.licenseID}`
                   : `${baseUrl}/license`;
    const method = license.licenseID ? "put" : "post";
    const response = await authFetch[method](url, request);
    return response.data;
  },
  

  async getLicense(licenseId: string | number): Promise<ILicense> {
    const response = await authFetch.get(`${baseUrl}/license/${licenseId}`);
    const data = response.data;
    return {
      licenseID: data.licenseID,
      licenseName: data.licenseName ?? '',
      licenseType: data.licenseType ?? '',
      licenseStatus: data.licenseStatus ?? '',
      startDate: data.startDate ?? '',
      endDate: data.endDate ?? '',
      cancelByDate: data.cancelByDate,
      renewByDate: data.renewByDate,
      autoRenew: data.autoRenew,
      renewalTerm: data.renewalTerm,
      annualCost: data.annualCost ?? '',
      seatQuantity: data.seatQuantity,
      creditQuantity: data.creditQuantity ?? '',
      totalValue: data.totalValue ?? '',
      paymentTerm: data.paymentTerm,
      paymentMethod: data.paymentMethod,
      initialPaymentDate: data.initialPaymentDate,
      approvalNotes: data.approvalNotes,
      otherNotes: data.otherNotes,
      isContract: data.isContract ?? false,
      isSubscription: data.isSubscription ?? false,
      applicationID: data.applicationID,
      fileId: data.fileId,
      file: data.file,
      vendor: data.vendor ?? '',
      cost: data.cost ?? '',
      licenseKey: data.licenseKey ?? '',
      licenseTypeID: data.licenseTypeID,
      licenseStatusID: data.licenseStatusID,
      renewalTermID: data.renewalTermID,
      paymentTermID: data.paymentTermID,
      paymentMethodID: data.paymentMethodID,
      isDisabled: false, 
    };
  },

  async detachLicense(
    applicationId: number | string,
    licenseId:     number | string
  ): Promise<void> {
    await authFetch.put(
      `${baseUrl}/${applicationId}/licenses/${licenseId}/detach`,
      {}
    );
  },

  async detachUser(applicationId: number | string, userId: number | string): Promise<void> {
    await authFetch.put(`${baseUrl}/${applicationId}/users/${userId}/detach`, {});
  },

  async detachLicenseFromUser(userId: number | string): Promise<void> {
    await authFetch.put(`/smp/application/users/${userId}/license/detach`, {});
  },

  async fetchLicenseStatuses(): Promise<{ value: string; label: string }[]> {
    const response = await authFetch.get(`${baseUrl}/license-statuses`);
    return response.data;
  },

  async fetchLicenseTypes(): Promise<{ value: string; label: string }[]> {
    const response = await authFetch.get(`${baseUrl}/license-types`);
    return response.data;
  },

  async fetchPaymentMethods(): Promise<{ value: string; label: string }[]> {
    const response = await authFetch.get(`${baseUrl}/payment-methods`);
    return response.data;
  },

  async fetchPaymentTerms(): Promise<{ value: string; label: string }[]> {
    const response = await authFetch.get(`${baseUrl}/payment-terms`);
    return response.data;
  },

  async fetchRenewalTerms(): Promise<{ value: string; label: string }[]> {
    const response = await authFetch.get(`${baseUrl}/renewal-terms`);
    return response.data;
  },



  async fetchUsers(
    applicationId: number | string,
    options: {
      page?: number;
      perPage?: number;
      excludeInactiveUsers?: boolean;
      search?: string;
      licenseIds?: string[];
      sort?: string; // e.g., "userName:asc,email:desc"
      // New native server-side filters
      applicationSubStatus?: string;
      appTierType?: string;
      employmentStatus?: string; // "Active" | "Inactive"
      dateAddedFrom?: string; // yyyy-MM-dd
      dateAddedTo?: string;   // yyyy-MM-dd
      licenseRemovedFrom?: string; // yyyy-MM-dd
      licenseRemovedTo?: string;   // yyyy-MM-dd
    }
  ): Promise<IUserRes> {
    const {
      page = 1,
      perPage = 10,
      excludeInactiveUsers = false,
      search,
      licenseIds,
      sort,
      applicationSubStatus,
      appTierType,
      employmentStatus,
      dateAddedFrom,
      dateAddedTo,
      licenseRemovedFrom,
      licenseRemovedTo,
    } = options;

    const params: Record<string, string> = {
      page: String(page),
      perPage: String(perPage),
      excludeInactiveUsers: String(excludeInactiveUsers),
    };
    if (search && search.trim() !== '') {
      params.search = search.trim();
    }
    if (sort && sort.trim() !== '') {
      params.sort = sort.trim();
    }
    if (applicationSubStatus && applicationSubStatus.trim() !== '') {
      params.applicationSubStatus = applicationSubStatus.trim();
    }
    if (appTierType && appTierType.trim() !== '') {
      params.appTierType = appTierType.trim();
    }
    if (employmentStatus && employmentStatus.trim() !== '') {
      params.employmentStatus = employmentStatus.trim();
    }
    if (dateAddedFrom && dateAddedFrom.trim() !== '') {
      params.dateAddedFrom = dateAddedFrom.trim();
    }
    if (dateAddedTo && dateAddedTo.trim() !== '') {
      params.dateAddedTo = dateAddedTo.trim();
    }
    if (licenseRemovedFrom && licenseRemovedFrom.trim() !== '') {
      params.licenseRemovedFrom = licenseRemovedFrom.trim();
    }
    if (licenseRemovedTo && licenseRemovedTo.trim() !== '') {
      params.licenseRemovedTo = licenseRemovedTo.trim();
    }

    
    let queryString = new URLSearchParams(params).toString();
    if (licenseIds && licenseIds.length > 0) {
      const licenseQs = licenseIds.map(id => `licenseIds=${encodeURIComponent(id)}`).join('&');
      queryString += (queryString ? '&' : '') + licenseQs;
    }

    const resp = await authFetch.get(
      `${baseUrl}/${applicationId}/users?${queryString}`
    );

    const data = resp.data;
    const records: IUser[] = data.records.map((r: any) => ({
      applicationUserID:    r.applicationUserID,
      applicationID:        r.applicationID,
      employeeID:           r.employeeID,
      userName:             r.userName,
      email:                r.email,
      employmentStatus:     r.employmentStatus ? 'Active' : 'Inactive',
      roleID:               r.roleID,
      applicationSubStatus: r.applicationSubStatus,
      assignedLicense:      r.assignedLicense,
      assignedLicenseName:  r.assignedLicenseName,
      appTierType:          r.appTierType,
      annualizedCost:       Number(r.annualizedCost),
      ytdValue:             r.ytdValue != null ? Number(r.ytdValue) : 0,
      allTimeSpend:         r.allTimeSpend != null ? Number(r.allTimeSpend) : 0,
      dateAdded:            r.dateAdded,
      accessRemoved:        r.accessRemoved,
      licenseRemoved:       r.licenseRemoved,
      approvalNotes:        r.approvalNotes,
      otherNotes:           r.otherNotes,
      createdAt:            r.createdAt,
    }));

    return {
      records,
      totalRecordCount: data.totalRecordCount,
    };
  },

  
  async getUser(userId: number | string): Promise<IUser> {
    const resp = await authFetch.get(`${baseUrl}/user/${userId}`)
    const r = resp.data
    return {
      applicationUserID:    r.applicationUserID,
      applicationID:        r.applicationID,
      employeeID:           r.employeeID,
      roleID:               r.roleID,
  
      employeeStatus:       r.employeeStatus,
      applicationSubStatus: r.applicationSubStatus,
      applicationSubStatusID: r.applicationSubStatusID,
      assignedLicense:      r.assignedLicense,
      appTierType:          r.appTierType,
      annualizedCost:       Number(r.annualizedCost),
  
      dateAdded:            r.dateAdded,
      accessRemoved:        r.accessRemoved,
      licenseRemoved:       r.licenseRemoved,
  
      approvalNotes:        r.approvalNotes,
      otherNotes:           r.otherNotes,
  
      createdAt:            r.createdAt,
    }
  },
  
  async upsertUser(user: IUser): Promise<IUser> {
    const payload = {
      applicationID:        user.applicationID,
      employeeID:           user.employeeID,
      roleID:               user.roleID,
      isActive:             user.employeeStatus,
      applicationSubStatus: user.applicationSubStatus,
      applicationSubStatusID: user.applicationSubStatusID,
      assignedLicense:      user.assignedLicense,
      appTierType:          user.appTierType,
      annualizedCost:       user.annualizedCost,
      dateAdded:            user.dateAdded,
      accessRemoved:        user.accessRemoved,
      licenseRemoved:       user.licenseRemoved,
      approvalNotes:        user.approvalNotes,
      otherNotes:           user.otherNotes,
      }
      const url = user.applicationUserID
      ? `${baseUrl}/user/${user.applicationUserID}`
      : `${baseUrl}/user`;
    const method = user.applicationUserID ? 'put' : 'post';

    const response = await authFetch[method](url, payload);
    return response.data as IUser;
  },
  

  async deleteUser(userId: number | string): Promise<void> {
    await authFetch.delete(`${baseUrl}/user/${userId}`);
  },

  async fetchUserStatusOptions(): Promise<{ value: string, label: string }[]> {
      const response = await authFetch.get(`${baseUrl}/user-status-options`);
      return response.data;
  },

  async fetchUserSubStatusOptions(): Promise<{ value: string, label: string }[]> {
      const response = await authFetch.get(`${baseUrl}/user-sub-status-options`);
      return response.data;
  },

  async fetchAppTierTypeOptions(): Promise<{ value: string, label: string }[]> {
      const response = await authFetch.get(`${baseUrl}/app-tier-type-options`);
      return response.data;
  },

  async fetchRoles(): Promise<{ value: number, label: string }[]> {
      const resp = await authFetch.get(`${baseUrl}/roles`);
      return resp.data;
  },

    
  async fetchEmployeeAdStatus(email: string): Promise<boolean> {
    const response = await authFetch.get(`${baseUrl}/employee-ad-status?email=${encodeURIComponent(email)}`);
    return response.data;
  },

  async fetchEmployeeAssignments(
    employeeId: number,
    options?: { page?: number; perPage?: number; showInactive?: boolean; sort?: string }
  ): Promise<{ records: any[]; totalRecordCount: number }> {
    const page = options?.page ?? 1;
    const perPage = options?.perPage ?? 10;
    const showInactive = options?.showInactive ?? false;
    const sort = options?.sort;
    
    const params: Record<string, string> = {
      page: String(page),
      perPage: String(perPage),
      showInactive: String(showInactive)
    };
    
    if (sort && sort.trim() !== '') {
      params.sort = sort.trim();
    }
    
    const qs = new URLSearchParams(params).toString();
    const response = await authFetch.get(`/smp/application/employee/${employeeId}/assignments?${qs}`);
    return response.data;
  }



};